/**
 * When Guardian last assessed a tenant, or null when it never has.
 *
 * `Tenants.LastScanAt` is not a reliable "assessed" signal on its own: the
 * tenant row stamps it at creation (`TenantConfigurationService` create path
 * and `SignUpService` set `LastScanAt = DateTime.UtcNow` beside `CreatedAt`),
 * and older rows carry the .NET `DateTime.MinValue` sentinel
 * ("0001-01-01T00:00:00"). Showing either as "Last assessed" would claim a
 * scan that never ran, so both read as no assessment.
 */

export interface TenantScanStamp {
  lastScanAt?: string | null
  createdAt?: string | null
}

/** A creation stamp lands within this much of CreatedAt; a real scan finishes minutes after onboarding at the earliest. */
const CREATION_STAMP_MS = 60_000
/** Anything earlier is a sentinel or a default, not a scan. */
const FIRST_REAL_YEAR = 2000

/** .NET serializes unspecified-kind DateTimes without an offset; read those as UTC so both stamps compare alike. */
function parseUtc(value: string): number | null {
  const ms = Date.parse(/(?:[zZ]|[+-]\d\d:?\d\d)$/.test(value) ? value : `${value}Z`)
  return Number.isNaN(ms) ? null : ms
}

/** The tenant's real last-assessment timestamp (the `lastScanAt` string as given), or null when there is none. */
export function lastAssessmentAt(tenant: TenantScanStamp | null | undefined): string | null {
  const raw = tenant?.lastScanAt
  if (!raw) return null
  const scan = parseUtc(raw)
  if (scan === null || new Date(scan).getUTCFullYear() < FIRST_REAL_YEAR) return null
  const created = tenant?.createdAt ? parseUtc(tenant.createdAt) : null
  if (created !== null && scan - created < CREATION_STAMP_MS) return null
  return raw
}
